/**
 * Dashboard refresh helpers (NAU-303).
 *
 * DashboardPage stays mounted across the in-app overview⇄Today toggle (`?view=`),
 * so the page-level queries' `refetchOnMount: "always"` doesn't re-fire on a
 * toggle the way it does on a real route remount. The page force-refetches those
 * page-level data queries on a view switch; `isDashboardPageDataKey` selects
 * them. The overview's own status/streak queries and the Today tabs' log/event
 * queries re-mount with their subtree, so they refresh on their own and are
 * excluded here.
 */

/**
 * Page-level data resources, matched against the request path that is the first
 * element of an Orval query key (`["/v1/nurseries/<id>/<resource>", params]`).
 * There is no compile-time link to the generated keys — keep in sync if the API
 * paths change (a generated-key test in the spec guards against drift).
 */
const DASHBOARD_DATA_KEY_PATHS = [
  "/scheduled-questionnaires",
  "/follow-up-actions",
  "/compliance-tasks/definitions",
] as const;

/**
 * True when a React Query key is exactly one of the dashboard's page-level data
 * list queries for the given nursery — used to force-refetch them on a view
 * switch. The match is on the full path, so resource detail keys
 * (e.g. `/follow-up-actions/<id>`) are not selected.
 */
export function isDashboardPageDataKey(
  queryKey: readonly unknown[],
  nurseryId: string,
): boolean {
  const key = queryKey[0];
  if (typeof key !== "string") return false;
  const base = `/v1/nurseries/${nurseryId}`;
  return DASHBOARD_DATA_KEY_PATHS.some((path) => key === `${base}${path}`);
}
